/**
 * Geolocation and GDPR Region Detection Utilities
 * Detects user's country via IP and determines GDPR compliance requirements
 */

/** EU/EEA/UK country codes requiring GDPR compliance */
export const GDPR_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE', // EU members
  'IS',
  'LI',
  'NO', // EEA
  'GB', // UK (post-Brexit)
] as const

export const TEST_MODE_KEYS = {
  FORCE_EU: 'test_forceEU',
  FORCE_NON_EU: 'test_forceNonEU',
  FORCE_FRANCE: 'test_forceFrance',
  FORCE_SPAIN: 'test_forceSpain',
} as const

const GEOLOCATION_API = {
  URL: 'https://ipapi.co/json/',
  TIMEOUT_MS: 5000,
  CACHE_KEY: 'ipp_geolocation_cache',
  CACHE_DURATION_MS: 3600000, // 1 hour
} as const

interface CachedGeolocation {
  countryCode: string | null
  timestamp: number
}

export interface GeolocationResult {
  countryCode: string | null
  isEU: boolean
  requiresConsent: boolean
  detectionMethod: 'test_override' | 'ip_api' | 'cache' | 'unknown'
}

/** Check if country requires GDPR compliance */
export const isGDPRCountry = (countryCode: string | null): boolean => {
  if (!countryCode) return false
  return GDPR_COUNTRIES.includes(countryCode.toUpperCase() as (typeof GDPR_COUNTRIES)[number])
}

const getCachedGeolocation = (): string | null => {
  try {
    const cached = sessionStorage.getItem(GEOLOCATION_API.CACHE_KEY)
    if (!cached) return null

    const data: CachedGeolocation = JSON.parse(cached)
    const age = Date.now() - data.timestamp

    if (age > GEOLOCATION_API.CACHE_DURATION_MS) {
      sessionStorage.removeItem(GEOLOCATION_API.CACHE_KEY)
      return null
    }

    return data.countryCode
  } catch (error) {
    console.warn('[Geolocation] Error reading cache:', error)
    return null
  }
}

const setCachedGeolocation = (countryCode: string | null): void => {
  try {
    const data: CachedGeolocation = {
      countryCode,
      timestamp: Date.now(),
    }
    sessionStorage.setItem(GEOLOCATION_API.CACHE_KEY, JSON.stringify(data))
  } catch (error) {
    console.warn('[Geolocation] Error writing cache:', error)
  }
}

/** Detect user's country via IP (cached for 1 hour) */
export const detectCountryViaIP = async (): Promise<string | null> => {
  const cached = getCachedGeolocation()
  if (cached !== null) {
    console.log('[Geolocation] Using cached country:', cached)
    return cached
  }

  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Geolocation API timeout')), GEOLOCATION_API.TIMEOUT_MS)
    })

    const response = await Promise.race([
      fetch(GEOLOCATION_API.URL, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      timeoutPromise,
    ])

    if (!response.ok) {
      console.warn('[Geolocation] API returned error:', response.status)
      return null
    }

    const data = await response.json()
    const countryCode = data.country_code || null

    setCachedGeolocation(countryCode)
    console.log('[Geolocation] Detected country via IP:', countryCode)
    return countryCode
  } catch (error) {
    console.error('[Geolocation] Error detecting country:', error)
    return null
  }
}

/**
 * Check for testing overrides (?forceEU=true, ?forceNonEU=true, ?forceFrance=true, ?forceSpain=true)
 * Overrides persist in session storage across navigation
 */
export const checkTestOverrides = (): {
  isTestMode: boolean
  countryCode: string | null
  isEU: boolean
} => {
  const urlParams = new URLSearchParams(window.location.search)
  const forceEUParam = urlParams.get('forceEU') === 'true'
  const forceNonEUParam = urlParams.get('forceNonEU') === 'true'
  const forceFranceParam = urlParams.get('forceFrance') === 'true'
  const forceSpainParam = urlParams.get('forceSpain') === 'true'

  // Clear all test flags first if any new flag is set
  if (forceEUParam || forceNonEUParam || forceFranceParam || forceSpainParam) {
    Object.values(TEST_MODE_KEYS).forEach(key => sessionStorage.removeItem(key))
  }

  if (forceFranceParam) {
    sessionStorage.setItem(TEST_MODE_KEYS.FORCE_FRANCE, 'true')
  } else if (forceSpainParam) {
    sessionStorage.setItem(TEST_MODE_KEYS.FORCE_SPAIN, 'true')
  } else if (forceEUParam) {
    sessionStorage.setItem(TEST_MODE_KEYS.FORCE_EU, 'true')
  } else if (forceNonEUParam) {
    sessionStorage.setItem(TEST_MODE_KEYS.FORCE_NON_EU, 'true')
  }

  // Check session storage for active test mode
  const forceFrance = sessionStorage.getItem(TEST_MODE_KEYS.FORCE_FRANCE) === 'true'
  const forceSpain = sessionStorage.getItem(TEST_MODE_KEYS.FORCE_SPAIN) === 'true'
  const forceEU = sessionStorage.getItem(TEST_MODE_KEYS.FORCE_EU) === 'true'
  const forceNonEU = sessionStorage.getItem(TEST_MODE_KEYS.FORCE_NON_EU) === 'true'

  if (forceFrance) {
    return { isTestMode: true, countryCode: 'FR', isEU: true }
  }

  if (forceSpain) {
    return { isTestMode: true, countryCode: 'ES', isEU: true }
  }

  if (forceEU) {
    return { isTestMode: true, countryCode: 'DE', isEU: true }
  }

  if (forceNonEU) {
    return { isTestMode: true, countryCode: 'CA', isEU: false }
  }

  return { isTestMode: false, countryCode: null, isEU: false }
}

/**
 * Detect user's region and determine if consent is required
 * Checks test overrides → cached geolocation → IP detection
 */
export const detectUserRegion = async (): Promise<GeolocationResult> => {
  const testOverride = checkTestOverrides()
  if (testOverride.isTestMode) {
    console.log('[Geolocation] TEST MODE: Forcing region', {
      country: testOverride.countryCode,
      isEU: testOverride.isEU,
    })
    return {
      countryCode: testOverride.countryCode,
      isEU: testOverride.isEU,
      requiresConsent: testOverride.isEU,
      detectionMethod: 'test_override',
    }
  }

  const countryCode = await detectCountryViaIP()
  const isEU = isGDPRCountry(countryCode)

  if (countryCode) {
    return {
      countryCode,
      isEU,
      requiresConsent: isEU,
      detectionMethod: getCachedGeolocation() ? 'cache' : 'ip_api',
    }
  }

  console.warn('[Geolocation] Could not detect region - defaulting to non-EU (implied consent)')
  return {
    countryCode: null,
    isEU: false,
    requiresConsent: false,
    detectionMethod: 'unknown',
  }
}

export const clearGeolocationCache = (): void => {
  sessionStorage.removeItem(GEOLOCATION_API.CACHE_KEY)
  console.log('[Geolocation] Cache cleared')
}

export const clearTestOverrides = (): void => {
  Object.values(TEST_MODE_KEYS).forEach(key => sessionStorage.removeItem(key))
  console.log('[Geolocation] Test overrides cleared')
}

/**
 * Get localized Terms and Privacy Policy URLs based on country code
 * Falls back to English for unsupported countries
 */
export const getLocalizedLegalLinks = (
  countryCode: string | null
): {
  termsUrl: string
  privacyUrl: string
} => {
  const upperCountryCode = countryCode?.toUpperCase()

  switch (upperCountryCode) {
    case 'FR':
      return {
        termsUrl:
          'https://privacyportal.onetrust.com/policies/4251b918-3443-43ee-ba76-031a6d308024?context=eyJkc1BvcnRhbElkIjoiY2QxYTBjNDEtYTUwNS00NjQyLWJiN2UtYTdhMjc4NjViYmZmIn0=',
        privacyUrl:
          'https://privacyportal.onetrust.com/policies/f41779bb-f6bc-4b87-8d36-b8afaec58ff6?context=eyJkc1BvcnRhbElkIjoiY2QxYTBjNDEtYTUwNS00NjQyLWJiN2UtYTdhMjc4NjViYmZmIn0=',
      }
    case 'ES':
      return {
        termsUrl:
          'https://privacyportal.onetrust.com/policies/b34c3449-1041-4ded-b77c-acc9b0e69575?context=eyJkc1BvcnRhbElkIjoiY2QxYTBjNDEtYTUwNS00NjQyLWJiN2UtYTdhMjc4NjViYmZmIn0=',
        privacyUrl:
          'https://privacyportal.onetrust.com/policies/6871d49e-3495-4291-9568-f19b903c02a4?context=eyJkc1BvcnRhbElkIjoiY2QxYTBjNDEtYTUwNS00NjQyLWJiN2UtYTdhMjc4NjViYmZmIn0=',
      }
    default:
      // Default to English (IPP-specific terms for B2B)
      return {
        termsUrl: 'https://docs.instacart.com/instacart/ipp-terms/',
        privacyUrl: 'https://www.instacart.com/company/privacy',
      }
  }
}
